import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, User, Phone, Mail, MapPin, Briefcase, Calendar, FileText, Clock, Shield, MessageSquare, Tag, RefreshCw, Download, Trash2, Paperclip, Scale, Archive, RotateCcw, Copy, StickyNote, Send, X, CheckCircle, Sparkles, ChevronDown, Save, PhoneCall, Target, Link2 } from "lucide-react";
import LegalAnalysisDisplay from "@/components/admin/LegalAnalysisDisplay";
import ShareLinkModal from "@/components/admin/ShareLinkModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  new: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  viewed: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  claimed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  closed: "bg-gray-500/10 text-gray-400 border-gray-500/20",
};

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("info");
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [reextracting, setReextracting] = useState(false);
  const [reextractMsg, setReextractMsg] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [matchedFirms, setMatchedFirms] = useState([]);
  const [firmsLoading, setFirmsLoading] = useState(false);
  const [sendHistory, setSendHistory] = useState([]);
  const [sendingToFirm, setSendingToFirm] = useState(null);
  const [sendMsg, setSendMsg] = useState(null);
  // Lead-score workspace
  const [boosters, setBoosters] = useState(null);
  const [enrich, setEnrich] = useState({});
  const [enrichNote, setEnrichNote] = useState("");
  const [savingEnrich, setSavingEnrich] = useState(false);
  const [enrichMsg, setEnrichMsg] = useState(null);
  const [genStrength, setGenStrength] = useState(false);
  // Outcome tracker (admin)
  const [outcome, setOutcome] = useState("pending");
  const [outcomeNotes, setOutcomeNotes] = useState("");
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [outcomeSaved, setOutcomeSaved] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const noteInputRef = useRef(null);
  const token = localStorage.getItem("adminToken");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/leads/${leadId}`, { headers });
        setLead(res.data);
        setOutcome(res.data.attorney_lead_status || "pending");
        setOutcomeNotes(res.data.outcome_notes || "");
      } catch (e) {
        if (e.response?.status === 401) navigate("/admin/login");
      } finally { setLoading(false); }
    })();
  }, [leadId]);

  useEffect(() => {
    if (activeSection === "documents") fetchDocuments();
    if (activeSection === "send") fetchMatchedFirms();
    if (activeSection === "scoring") fetchBoosters();
  }, [activeSection]);

  const fetchBoosters = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/${leadId}/score-boosters`, { headers });
      setBoosters(res.data);
    } catch { /* non-fatal */ }
  };

  const saveEnrichment = async () => {
    const fields = Object.fromEntries(Object.entries(enrich).filter(([, v]) => v && v.trim()));
    if (Object.keys(fields).length === 0 && !enrichNote.trim()) {
      setEnrichMsg({ type: "warn", text: "Enter some info or a note before saving." });
      return;
    }
    setSavingEnrich(true);
    setEnrichMsg(null);
    try {
      const res = await axios.post(`${API}/admin/leads/${leadId}/enrich`,
        { fields, note: enrichNote.trim() || undefined }, { headers });
      setLead(res.data.lead);
      setEnrich({});
      setEnrichNote("");
      fetchBoosters();
      setEnrichMsg({ type: "ok", text: `Saved & re-scored — new overall ${res.data.case_score?.overall_score ?? "—"}/100.` });
      setTimeout(() => setEnrichMsg(null), 6000);
    } catch {
      setEnrichMsg({ type: "err", text: "Failed to save. Try again." });
    } finally { setSavingEnrich(false); }
  };

  const generateStrengthen = async () => {
    setGenStrength(true);
    try {
      const res = await axios.post(`${API}/admin/leads/${leadId}/strengthen`, {}, { headers });
      setLead((p) => ({ ...p, strengthen_analysis: res.data.strengthen_analysis }));
    } catch { setError?.("Failed to generate suggestions"); }
    finally { setGenStrength(false); }
  };

  const saveOutcome = async () => {
    setSavingOutcome(true); setOutcomeSaved(false);
    try {
      await axios.put(`${API}/admin/leads/${leadId}/outcome`, { outcome, notes: outcomeNotes }, { headers });
      setLead((p) => ({ ...p, attorney_lead_status: outcome, outcome_notes: outcomeNotes }));
      setOutcomeSaved(true);
      setTimeout(() => setOutcomeSaved(false), 3000);
    } catch { setError?.("Failed to save outcome"); }
    finally { setSavingOutcome(false); }
  };

  // Admin's OWN call tracking (independent of firms): called / confirmed / reachable
  const toggleAdminCall = async (flag) => {
    const cur = lead.admin_call || {};
    const nextVal = !cur[flag];
    setLead((p) => ({ ...p, admin_call: { ...cur, [flag]: nextVal } }));
    try { await axios.put(`${API}/admin/leads/${leadId}/call-status`, { [flag]: nextVal }, { headers }); }
    catch { setLead((p) => ({ ...p, admin_call: cur })); }
  };

  const fetchMatchedFirms = async () => {
    setFirmsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads/${leadId}/matched-firms`, { headers });
      setMatchedFirms(res.data);
    } catch {} finally { setFirmsLoading(false); }
    fetchSendHistory();
  };

  const fetchSendHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/${leadId}/distribution-history`, { headers });
      setSendHistory(res.data || []);
    } catch {}
  };

  const sendToFirm = async (firmId) => {
    setSendingToFirm(firmId); setSendMsg(null);
    try {
      await axios.post(`${API}/admin/leads/${leadId}/assign`, { firm_id: firmId, method: "manual" }, { headers });
      setSendMsg({ type: "success", text: "Lead sent." });
      fetchMatchedFirms();
      const res = await axios.get(`${API}/admin/leads/${leadId}`, { headers });
      setLead(res.data);
    } catch (e) {
      setSendMsg({ type: "error", text: e.response?.data?.detail || "Failed to send lead." });
    } finally { setSendingToFirm(null); }
  };

  const unsendFromFirm = async (firmId) => {
    setSendingToFirm(firmId); setSendMsg(null);
    try {
      await axios.post(`${API}/admin/leads/${leadId}/unsend`, { firm_id: firmId }, { headers });
      setSendMsg({ type: "success", text: "Lead unsent — firm returned to its list." });
      fetchMatchedFirms();
      const res = await axios.get(`${API}/admin/leads/${leadId}`, { headers });
      setLead(res.data);
    } catch (e) {
      setSendMsg({ type: "error", text: e.response?.data?.detail || "Failed to unsend." });
    } finally { setSendingToFirm(null); }
  };

  const fetchDocuments = async () => {
    setDocsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads/${leadId}/documents`, { headers });
      setDocuments(res.data.documents);
    } catch {} finally { setDocsLoading(false); }
  };

  const downloadDocument = (docId, filename) => {
    window.open(`${API}/admin/documents/${docId}/download`, "_blank");
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await axios.delete(`${API}/admin/documents/${docId}`, { headers });
      setDocuments(prev => prev.filter(d => d.doc_id !== docId));
    } catch {}
  };

  const updateStatus = async (newStatus) => {
    try {
      await axios.put(`${API}/admin/leads/${leadId}/status`, { lead_status: newStatus }, { headers });
      setLead(prev => ({ ...prev, lead_status: newStatus }));
    } catch {}
  };

  const handleReanalyze = async () => {
    setReanalyzing(true);
    try {
      const res = await axios.post(`${API}/admin/rescore-lead/${leadId}`, {}, { headers });
      setLead(prev => ({ ...prev, case_score: res.data.case_score, legal_analysis: res.data.legal_analysis }));
    } catch {} finally { setReanalyzing(false); }
  };

  const handleReextract = async () => {
    setReextracting(true);
    setReextractMsg(null);
    try {
      const res = await axios.post(`${API}/admin/leads/${leadId}/reextract`, {}, { headers });
      const { fields_updated, lead: updated } = res.data;
      setLead(prev => ({ ...prev, ...updated }));
      setReextractMsg(fields_updated.length > 0
        ? `Recovered: ${fields_updated.join(", ")}`
        : "No new fields found to extract.");
    } catch (e) {
      setReextractMsg(e.response?.data?.detail || "Re-extraction failed.");
    } finally { setReextracting(false); }
  };

  const handleArchive = async () => {
    setArchiving(true);
    try {
      await axios.post(`${API}/admin/leads/${leadId}/archive`, {}, { headers });
      setLead(prev => ({ ...prev, archived: true }));
    } catch { } finally { setArchiving(false); }
  };

  const handleRestore = async () => {
    setArchiving(true);
    try {
      await axios.post(`${API}/admin/leads/${leadId}/restore`, {}, { headers });
      setLead(prev => ({ ...prev, archived: false }));
    } catch { } finally { setArchiving(false); }
  };

  const handleDeletePermanently = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/leads/${leadId}`, { headers });
      navigate("/admin");
    } catch { setDeleting(false); }
  };

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setAddingNote(true);
    try {
      const res = await axios.post(`${API}/admin/leads/${leadId}/notes`, { text }, { headers });
      setLead(prev => ({ ...prev, notes: [...(prev.notes || []), res.data.note] }));
      setNoteText("");
    } catch { } finally { setAddingNote(false); }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/admin/leads/${leadId}/notes/${noteId}`, { headers });
      setLead(prev => ({ ...prev, notes: (prev.notes || []).filter(n => n.note_id !== noteId) }));
    } catch { }
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    try { return new Date(ts).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }); } catch { return ts; }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.includes("pdf")) return "📄";
    if (contentType?.includes("image")) return "🖼️";
    if (contentType?.includes("word") || contentType?.includes("doc")) return "📝";
    if (contentType?.includes("sheet") || contentType?.includes("excel")) return "📊";
    return "📎";
  };

  if (loading) return (
    <div className="flex items-center justify-center bg-background" style={{ height: '100dvh' }} data-testid="lead-loading">
      <RefreshCw className="w-6 h-6 text-primary animate-spin" />
    </div>
  );
  if (!lead) return (
    <div className="flex items-center justify-center bg-background" style={{ height: '100dvh' }} data-testid="lead-not-found">
      <p className="text-muted-foreground">Lead not found</p>
    </div>
  );

  const infoFields = [
    { icon: User, label: "Full Name", value: lead.name },
    { icon: Phone, label: "Phone", value: lead.phone },
    { icon: Mail, label: "Email", value: lead.email },
    { icon: MapPin, label: "Location", value: lead.location },
    { icon: Briefcase, label: "Case Type", value: lead.case_type },
    { icon: Tag, label: "Category", value: lead.case_category },
    { icon: User, label: "Parties", value: lead.parties_involved },
    { icon: Calendar, label: "Incident Date", value: lead.incident_date },
    { icon: Clock, label: "Current Status", value: lead.current_status },
    { icon: FileText, label: "Documents (described)", value: lead.documents },
    { icon: MessageSquare, label: "Prior Consult", value: lead.prior_consultation },
    { icon: Phone, label: "Contact Pref.", value: lead.contact_preference },
    { icon: Clock, label: "Availability", value: lead.availability },
    { icon: Shield, label: "Recommended Atty", value: lead.recommended_attorney_type },
    { icon: FileText, label: "Fee Preference", value: lead.fee_type },
    { icon: FileText, label: "Budget", value: lead.fee_budget },
  ].filter(f => f.value);

  const docCount = documents.length || lead.uploaded_documents?.length || 0;

  return (
    <div className="bg-background flex flex-col" style={{ minHeight: '100dvh' }} data-testid="lead-detail-page">
      {/* Header */}
      <header className="border-b border-border/30 bg-background/90 backdrop-blur-xl sticky top-0 z-20" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-3">
          <button onClick={() => navigate("/admin/dashboard")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /><span className="hidden sm:inline">Dashboard</span>
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{lead.name || "Anonymous Client"}</p>
            <p className="text-xs text-muted-foreground truncate">{lead.case_category || lead.case_type || "—"}</p>
          </div>
          <select value={lead.lead_status || "new"} onChange={(e) => updateStatus(e.target.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border cursor-pointer focus:outline-none ${statusColors[lead.lead_status] || statusColors.new} bg-transparent flex-shrink-0`}
            data-testid="status-selector">
            {["new","viewed","claimed","closed"].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
          </select>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-8 w-full pb-8">
        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-secondary/30 rounded-xl border border-border/30 w-fit mb-4 sm:mb-6 overflow-x-auto">
          {[
            { id: "info", label: "Lead Info" },
            { id: "scoring", label: "Lead Score" },
            { id: "send", label: "Send to Firm" },
            { id: "notes", label: `Notes${(lead.notes?.length || 0) > 0 ? ` (${lead.notes.length})` : ""}` },
            { id: "legal", label: "Legal Analysis" },
            { id: "transcript", label: `Chat (${lead.conversation?.length || 0})` },
            { id: "documents", label: `Documents${docCount > 0 ? ` (${docCount})` : ""}` },
          ].map(({ id, label }) => (
            <button key={id} onClick={() => setActiveSection(id)}
              className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${activeSection === id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`tab-${id}`}>{label}</button>
          ))}
        </div>

        {/* Duplicate warning */}
        {lead.is_duplicate && (
          <div className="mb-4 p-3 rounded-xl border border-orange-500/30 bg-orange-500/5 flex items-center gap-3" data-testid="duplicate-warning">
            <Copy className="w-4 h-4 text-orange-400 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-orange-400">Duplicate Submission</p>
              <p className="text-xs text-muted-foreground">This lead shares a phone or email with an existing lead.
                {lead.duplicate_of && <button onClick={() => navigate(`/admin/leads/${lead.duplicate_of}`)} className="ml-1 underline text-orange-400 hover:text-orange-300">View original</button>}
              </p>
            </div>
          </div>
        )}

        {/* Archive/Delete bar */}
        <div className="mb-4 flex items-center justify-end gap-2 flex-wrap">
          <button
            onClick={() => setShowShareModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-all text-xs font-medium"
            data-testid="open-share-modal-btn"
            title="Generate a shareable read-only link"
          >
            <Link2 className="w-3 h-3" />Share Lead
          </button>
          {lead.archived ? (
            <>
              <button onClick={handleRestore} disabled={archiving}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 transition-all text-xs font-medium disabled:opacity-50"
                data-testid="restore-btn">
                <RotateCcw className="w-3 h-3" />{archiving ? "Restoring..." : "Restore Lead"}
              </button>
              {!showDeleteConfirm ? (
                <button onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive hover:bg-destructive/20 transition-all text-xs font-medium"
                  data-testid="delete-permanently-btn">
                  <Trash2 className="w-3 h-3" />Delete Permanently
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-destructive">Are you sure?</span>
                  <button onClick={handleDeletePermanently} disabled={deleting}
                    className="px-3 py-1.5 rounded-lg bg-destructive text-white text-xs font-medium disabled:opacity-50"
                    data-testid="confirm-delete-btn">
                    {deleting ? "Deleting..." : "Yes, delete"}
                  </button>
                  <button onClick={() => setShowDeleteConfirm(false)} className="px-3 py-1.5 rounded-lg bg-secondary/40 text-xs">Cancel</button>
                </div>
              )}
            </>
          ) : (
            <button onClick={handleArchive} disabled={archiving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary/40 border border-border/30 text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-all text-xs font-medium disabled:opacity-50"
              data-testid="archive-btn">
              <Archive className="w-3 h-3" />{archiving ? "Archiving..." : "Archive Lead"}
            </button>
          )}
        </div>

        {/* Info */}
        {activeSection === "info" && (
          <div className="space-y-3 animate-fade-in" data-testid="lead-info-section">
            {/* Update Outcome — admin controls the lead outcome (shared on the lead) */}
            <div className="p-4 rounded-xl border border-border/40 bg-card/30" data-testid="outcome-panel">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold">Update Outcome</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr_auto] gap-3 items-end">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Status</label>
                  <div className="relative">
                    <select value={outcome} onChange={(e) => setOutcome(e.target.value)}
                      className="w-full py-2 pl-3 pr-8 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 appearance-none">
                      <option value="pending">New / Pending</option>
                      <option value="contacted">Contacted Client</option>
                      <option value="won">Won — Client Retained</option>
                      <option value="lost">Lost — Did Not Retain</option>
                      <option value="no_response">No Response</option>
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Outcome notes (optional)</label>
                  <input value={outcomeNotes} onChange={(e) => setOutcomeNotes(e.target.value)}
                    placeholder="e.g. Retained 6/14, signed PI agreement"
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                </div>
                <button onClick={saveOutcome} disabled={savingOutcome}
                  className="py-2 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition disabled:opacity-50 flex items-center justify-center gap-1.5">
                  {outcomeSaved ? <><CheckCircle className="w-4 h-4" /> Saved</> : savingOutcome ? "Saving…" : <><Save className="w-4 h-4" /> Save</>}
                </button>
              </div>
            </div>

            {/* Call & Contact — admin's own tracking (independent of firms) */}
            <div className="p-4 rounded-xl border border-border/40 bg-card/30" data-testid="admin-call-panel">
              <div className="flex items-center gap-2 mb-1">
                <Phone className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold">Call &amp; Contact</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Called &amp; Reachable are your own. <strong className="text-foreground/80">Contact Info Confirmed</strong> is shared with every assigned firm.</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { flag: "called", label: "Called", on: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", icon: Phone, shared: false },
                  { flag: "confirmed", label: "Contact Info Confirmed", on: "bg-sky-500/15 text-sky-400 border-sky-500/30", icon: CheckCircle, shared: true },
                  { flag: "reachable", label: "Reachable", on: "bg-violet-500/15 text-violet-300 border-violet-500/30", icon: PhoneCall, shared: false },
                ].map(({ flag, label, on, icon: Icon, shared }) => {
                  const active = !!lead.admin_call?.[flag];
                  return (
                    <button key={flag} onClick={() => toggleAdminCall(flag)}
                      title={shared ? "Shared with all assigned firms (read-only for them)" : "Your own tracking"}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition ${active ? on : "bg-gray-500/10 text-gray-400 border-gray-500/20 hover:bg-gray-500/20"}`}
                      data-testid={`call-flag-${flag}`}>
                      <Icon className="w-3.5 h-3.5" />
                      {active ? <CheckCircle className="w-3 h-3" /> : null}
                      {label}
                      {shared && <span className="text-[9px] font-bold opacity-70">· shared</span>}
                    </button>
                  );
                })}
              </div>
              {!lead.admin_call?.called && <p className="text-[11px] text-muted-foreground/70 mt-2">Status: Not Called</p>}
            </div>
            {/* Missing data warning + Re-Extract button */}
            {(!lead.name || !lead.email || !lead.phone) && (
              <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/5 flex items-center justify-between gap-3" data-testid="missing-data-banner">
                <div>
                  <p className="text-xs font-medium text-amber-400">Incomplete lead data</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Missing: {[!lead.name && "name", !lead.email && "email", !lead.phone && "phone"].filter(Boolean).join(", ")}
                  </p>
                  {reextractMsg && <p className="text-xs text-emerald-400 mt-1">{reextractMsg}</p>}
                </div>
                <button onClick={handleReextract} disabled={reextracting}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 transition-all text-xs font-medium disabled:opacity-50"
                  data-testid="reextract-btn">
                  <RefreshCw className={`w-3 h-3 ${reextracting ? "animate-spin" : ""}`} />
                  {reextracting ? "Extracting..." : "Re-Extract Data"}
                </button>
              </div>
            )}
            {/* Case Score Card */}
            {lead.case_score && (
              <div className="p-4 rounded-xl border border-border/30 bg-card/20" data-testid="case-score-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">AI Case Score</span>
                  <button onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/admin/rescore-lead/${leadId}`, {}, { headers });
                      setLead(prev => ({ ...prev, case_score: res.data.case_score }));
                    } catch {}
                  }} className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" />Re-score
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[
                    { label: "Overall", value: lead.case_score.overall_score, suffix: "/100" },
                    { label: "Win Prob.", value: lead.case_score.win_probability != null ? `${lead.case_score.win_probability}%` : "—", raw: lead.case_score.win_probability },
                    { label: "Clarity", value: lead.case_score.clarity_score, suffix: "/100" },
                    { label: "Urgency", value: lead.case_score.urgency_score, suffix: "/100" },
                  ].map(({ label, value, suffix, raw }) => {
                    const num = raw ?? (typeof value === 'number' ? value : null);
                    const color = num == null ? "text-muted-foreground" : num >= 70 ? "text-emerald-400" : num >= 40 ? "text-amber-400" : "text-red-400";
                    return (
                      <div key={label} className="text-center p-2 rounded-lg bg-secondary/20">
                        <div className={`text-lg font-bold ${color}`}>{value ?? "—"}{value != null && suffix && typeof value === 'number' ? suffix : ""}</div>
                        <div className="text-xs text-muted-foreground">{label}</div>
                      </div>
                    );
                  })}
                </div>
                {lead.case_score.rationale && (
                  <p className="text-xs text-muted-foreground italic leading-relaxed">{lead.case_score.rationale}</p>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {infoFields.map(({ icon: Icon, label, value }) => (
                <div key={label} className="p-3 sm:p-4 rounded-xl border border-border/30 bg-card/20">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon className="w-3.5 h-3.5 text-primary/60" />
                    <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</span>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed break-words">{value}</p>
                </div>
              ))}
            </div>
            {lead.fact_summary && (
              <div className="p-3 sm:p-4 rounded-xl border border-border/30 bg-card/20">
                <div className="flex items-center gap-2 mb-1.5">
                  <FileText className="w-3.5 h-3.5 text-primary/60" />
                  <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Fact Summary</span>
                </div>
                <p className="text-sm text-foreground leading-relaxed">{lead.fact_summary}</p>
              </div>
            )}

            {/* Desired Outcome — what the client wants */}
            <div className="p-3 sm:p-4 rounded-xl border border-primary/20 bg-primary/[0.04]" data-testid="desired-outcome">
              <div className="flex items-center gap-2 mb-1.5">
                <Target className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs text-primary uppercase tracking-wider font-semibold">Desired Outcome</span>
              </div>
              <p className="text-sm text-foreground leading-relaxed">
                {lead.desired_outcome || lead.case_analysis?.desired_outcome
                  || <span className="text-muted-foreground italic">Not captured during intake.</span>}
              </p>
            </div>
          </div>
        )}

        {/* Lead Score workspace */}
        {activeSection === "scoring" && (() => {
          const cs = lead.case_score || {};
          const la = lead.legal_analysis || {};
          const overall = cs.overall_score;
          const ring = overall == null ? "text-muted-foreground border-border"
            : overall >= 70 ? "text-emerald-400 border-emerald-500/40"
            : overall >= 40 ? "text-amber-400 border-amber-500/40"
            : "text-red-400 border-red-500/40";
          const subColor = (n) => n == null ? "text-muted-foreground" : n >= 70 ? "text-emerald-400" : n >= 40 ? "text-amber-400" : "text-red-400";
          // Derive positive / negative factors
          const positives = [];
          const negatives = [];
          if (cs.win_probability >= 60) positives.push(`Strong win probability (${cs.win_probability}%)`);
          else if (cs.win_probability != null && cs.win_probability < 40) negatives.push(`Low win probability (${cs.win_probability}%)`);
          if (cs.clarity_score >= 70) positives.push("Case facts are clear and complete");
          else if (cs.clarity_score != null && cs.clarity_score < 50) negatives.push("Case details are incomplete / unclear");
          if (cs.urgency_score >= 70) positives.push("Time-sensitive — high urgency");
          (la.case_strengths || []).forEach(s => positives.push(s));
          (la.red_flags || []).forEach(s => negatives.push(s));
          if (la.sol_status === "At Risk" || la.sol_status === "Potentially Expired") negatives.push(`Statute of limitations: ${la.sol_status}`);

          const enrichFields = [
            { key: "employment_status", label: "Employment Status", placeholder: "e.g. Full-time, unemployed, self-employed" },
            { key: "income", label: "Income", placeholder: "e.g. $65k/yr, hourly, unable to pay" },
            { key: "incident_date", label: "Incident Date", placeholder: "When did it happen?" },
            { key: "current_status", label: "Current Status", placeholder: "Where does the matter stand now?" },
            { key: "parties_involved", label: "Parties Involved", placeholder: "Other people / companies involved" },
            { key: "documents", label: "Documents / Evidence", placeholder: "What do they have on hand?" },
            { key: "phone", label: "Phone", placeholder: "Best contact number" },
            { key: "email", label: "Email", placeholder: "Email address" },
            { key: "location", label: "Location", placeholder: "City / state / jurisdiction" },
          ];

          return (
            <div className="space-y-4 animate-fade-in" data-testid="scoring-section">
              {/* Prominent score display */}
              <div className="p-5 rounded-2xl border border-border/30 bg-card/20 flex flex-col sm:flex-row items-center gap-5">
                <div className={`w-24 h-24 rounded-full border-4 ${ring} flex flex-col items-center justify-center flex-shrink-0`}>
                  <span className="text-3xl font-bold leading-none">{overall ?? "—"}</span>
                  <span className="text-[10px] text-muted-foreground mt-0.5">/ 100</span>
                </div>
                <div className="flex-1 w-full">
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {[
                      { label: "Win Prob.", v: cs.win_probability != null ? `${cs.win_probability}%` : "—", n: cs.win_probability },
                      { label: "Clarity", v: cs.clarity_score ?? "—", n: cs.clarity_score },
                      { label: "Urgency", v: cs.urgency_score ?? "—", n: cs.urgency_score },
                    ].map(({ label, v, n }) => (
                      <div key={label} className="text-center p-2 rounded-lg bg-secondary/20">
                        <div className={`text-lg font-bold ${subColor(n)}`}>{v}</div>
                        <div className="text-xs text-muted-foreground">{label}</div>
                      </div>
                    ))}
                  </div>
                  {cs.rationale && <p className="text-xs text-muted-foreground italic leading-relaxed">{cs.rationale}</p>}
                  {!lead.case_score && <p className="text-xs text-muted-foreground">No score yet — add info below and save to generate one.</p>}
                </div>
              </div>

              {/* Breakdown: positive / negative */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5">
                  <h4 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5" /> Positive Factors
                  </h4>
                  {positives.length ? (
                    <ul className="space-y-1.5">
                      {positives.map((p, i) => <li key={i} className="text-xs text-foreground/90 flex gap-1.5"><span className="text-emerald-400">+</span>{p}</li>)}
                    </ul>
                  ) : <p className="text-xs text-muted-foreground">None identified yet.</p>}
                </div>
                <div className="p-4 rounded-xl border border-red-500/20 bg-red-500/5">
                  <h4 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <X className="w-3.5 h-3.5" /> Negative Factors
                  </h4>
                  {negatives.length ? (
                    <ul className="space-y-1.5">
                      {negatives.map((p, i) => <li key={i} className="text-xs text-foreground/90 flex gap-1.5"><span className="text-red-400">–</span>{p}</li>)}
                    </ul>
                  ) : <p className="text-xs text-muted-foreground">None identified.</p>}
                </div>
              </div>

              {/* Strengthen the case — AI questions to ask to raise the win rate & case value */}
              {(() => {
                const st = lead.strengthen_analysis;
                return (
                  <div className="p-4 rounded-xl border border-violet-500/25 bg-violet-500/[0.06]">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h4 className="text-xs font-semibold text-violet-300 uppercase tracking-wider flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" /> Strengthen the Case · Win-Rate Boosters
                      </h4>
                      <button onClick={generateStrengthen} disabled={genStrength}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-500/15 text-violet-200 border border-violet-500/30 text-xs font-semibold hover:bg-violet-500/25 transition disabled:opacity-50">
                        <RefreshCw className={`w-3.5 h-3.5 ${genStrength ? "animate-spin" : ""}`} />
                        {genStrength ? "Analyzing…" : st ? "Regenerate" : "Generate suggestions"}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">AI-suggested questions to ask and info to gather that make this case bigger, more solid, and more likely to win.</p>
                    {!st ? (
                      <p className="text-xs text-muted-foreground italic">No suggestions yet — click <strong>Generate suggestions</strong> for case-specific guidance.</p>
                    ) : (
                      <div className="space-y-3">
                        {st.summary && <p className="text-sm text-foreground/90 leading-relaxed bg-violet-500/10 rounded-lg px-3 py-2 border border-violet-500/20">💡 {st.summary}</p>}
                        {Array.isArray(st.questions_to_ask) && st.questions_to_ask.length > 0 && (
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-violet-300/80 mb-1">Questions to Ask the Client</p>
                            <ul className="space-y-1">{st.questions_to_ask.map((q, i) => <li key={i} className="text-xs text-foreground/85 flex gap-1.5"><span className="text-violet-400">?</span>{q}</li>)}</ul>
                          </div>
                        )}
                        {Array.isArray(st.win_rate_levers) && st.win_rate_levers.length > 0 && (
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-400/80 mb-1">What Raises the Win Rate</p>
                            <ul className="space-y-1">{st.win_rate_levers.map((q, i) => <li key={i} className="text-xs text-foreground/85 flex gap-1.5"><span className="text-emerald-400">↑</span>{q}</li>)}</ul>
                          </div>
                        )}
                        {Array.isArray(st.value_drivers) && st.value_drivers.length > 0 && (
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-400/80 mb-1">What Makes the Case Bigger</p>
                            <ul className="space-y-1">{st.value_drivers.map((q, i) => <li key={i} className="text-xs text-foreground/85 flex gap-1.5"><span className="text-amber-400">$</span>{q}</li>)}</ul>
                          </div>
                        )}
                        {st.generated_at && <p className="text-[10px] text-muted-foreground/60">Generated {new Date(st.generated_at).toLocaleString()}</p>}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Improve the score */}
              <div className="p-4 rounded-xl border border-primary/20 bg-primary/5">
                <h4 className="text-xs font-semibold text-primary uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" /> Improve This Lead's Score
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">Missing information to collect</p>
                    {boosters?.missing_info?.length ? (
                      <ul className="space-y-1">
                        {boosters.missing_info.map((m, i) => <li key={i} className="text-xs text-foreground/80 flex gap-1.5"><span className="text-amber-400">•</span>{m.label}</li>)}
                      </ul>
                    ) : <p className="text-xs text-muted-foreground">All key fields collected. 🎉</p>}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">Recommended actions</p>
                    {boosters?.recommended_actions?.length ? (
                      <ul className="space-y-1">
                        {boosters.recommended_actions.map((a, i) => <li key={i} className="text-xs text-foreground/80 flex gap-1.5"><span className="text-primary">→</span>{a}</li>)}
                      </ul>
                    ) : <p className="text-xs text-muted-foreground">Run a re-analysis on the Legal tab for tailored steps.</p>}
                  </div>
                </div>
              </div>

              {/* Data collection — editable, rescore on save */}
              <div className="p-4 rounded-xl border border-border/30 bg-card/20">
                <h4 className="text-xs font-semibold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-primary/60" /> Collect Info While On The Call
                </h4>
                <p className="text-xs text-muted-foreground mb-3">Fill anything you learn. Saving merges it into the lead and automatically re-scores.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {enrichFields.map(({ key, label, placeholder }) => (
                    <div key={key}>
                      <label className="text-xs text-muted-foreground mb-1 block">{label}{lead[key] ? <span className="text-emerald-400/70 ml-1">(on file)</span> : ""}</label>
                      <input
                        value={enrich[key] ?? ""}
                        onChange={(e) => setEnrich(prev => ({ ...prev, [key]: e.target.value }))}
                        placeholder={lead[key] ? `Current: ${String(lead[key]).slice(0, 40)}` : placeholder}
                        className="w-full px-3 py-2 rounded-lg bg-background border border-border text-xs focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <label className="text-xs text-muted-foreground mb-1 block">Case Details / Notes</label>
                  <textarea
                    value={enrichNote}
                    onChange={(e) => setEnrichNote(e.target.value)}
                    rows={3}
                    placeholder="Anything else worth recording about the case or the call..."
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
                  />
                </div>
                {enrichMsg && (
                  <p className={`text-xs mt-2 ${enrichMsg.type === "ok" ? "text-emerald-400" : enrichMsg.type === "err" ? "text-red-400" : "text-amber-400"}`}>
                    {enrichMsg.text}
                  </p>
                )}
                <div className="flex justify-end mt-3">
                  <button onClick={saveEnrichment} disabled={savingEnrich}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition disabled:opacity-50">
                    <RefreshCw className={`w-3.5 h-3.5 ${savingEnrich ? "animate-spin" : ""}`} />
                    {savingEnrich ? "Saving & re-scoring..." : "Save & Re-score"}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Legal Analysis */}
        {activeSection === "legal" && (
          <LegalAnalysisDisplay
            analysis={lead.legal_analysis}
            onReanalyze={handleReanalyze}
            reanalyzing={reanalyzing}
          />
        )}

        {/* Transcript */}
        {activeSection === "transcript" && (
          <div className="space-y-3 animate-fade-in" data-testid="transcript-section">
            {lead.conversation && lead.conversation.length > 0 && (
              <div className="flex justify-end mb-2">
                <button
                  onClick={() => {
                    const text = lead.conversation.map(m =>
                      `${m.role === "assistant" ? "Sasha" : "Client"}: ${m.content}`
                    ).join("\n\n");
                    navigator.clipboard.writeText(text).then(() => {
                      setCopySuccess(true);
                      setTimeout(() => setCopySuccess(false), 2000);
                    });
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-secondary/60 hover:bg-secondary border border-border/30 transition-all"
                  data-testid="copy-full-convo-btn"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {copySuccess ? "Copied!" : "Copy Full Convo"}
                </button>
              </div>
            )}
            {(!lead.conversation || lead.conversation.length === 0) ? (
              <div className="text-center py-10 text-muted-foreground text-sm">No transcript available</div>
            ) : (
              lead.conversation.map((msg, i) => (
                <div key={i} className={`flex gap-2 sm:gap-3 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`} data-testid={`transcript-msg-${i}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5 ${msg.role === "assistant" ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}>
                    {msg.role === "assistant" ? "A" : "C"}
                  </div>
                  <div className={`max-w-[85%] flex flex-col gap-1 ${msg.role === "user" ? "items-end" : "items-start"}`}>
                    <div className={`px-3 sm:px-4 py-2.5 sm:py-3 rounded-2xl text-sm leading-relaxed ${msg.role === "user" ? "bg-primary/20 text-foreground rounded-tr-sm" : "bg-secondary/40 text-foreground rounded-tl-sm border border-border/20"}`}>
                      {msg.content}
                    </div>
                    <span className="text-xs text-muted-foreground/50 px-1">{formatTime(msg.timestamp)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Documents */}
        {activeSection === "documents" && (
          <div className="space-y-3 animate-fade-in" data-testid="documents-section">
            {docsLoading ? (
              <div className="text-center py-10 text-muted-foreground">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />Loading documents...
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground border border-border/30 rounded-xl">
                <Paperclip className="w-8 h-8 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No documents uploaded</p>
                <p className="text-xs mt-1">Documents uploaded during intake will appear here</p>
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div key={doc.doc_id} className="flex items-center gap-3 p-3 sm:p-4 rounded-xl border border-border/30 bg-card/20 hover:bg-card/30 transition-all" data-testid={`doc-${doc.doc_id}`}>
                    <div className="text-2xl flex-shrink-0">{getFileIcon(doc.content_type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.original_filename}</p>
                      <p className="text-xs text-muted-foreground">{formatBytes(doc.size)} · {new Date(doc.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <button onClick={() => downloadDocument(doc.doc_id, doc.original_filename)}
                        className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-all" title="Download"
                        data-testid={`download-doc-${doc.doc_id}`}>
                        <Download className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteDocument(doc.doc_id)}
                        className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all" title="Delete"
                        data-testid={`delete-doc-${doc.doc_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Send to Attorney Offices — sectioned, scannable */}
        {activeSection === "send" && (() => {
          const matched = matchedFirms.filter(f => f.is_match);
          const readyToSend = matched.filter(f => !f.already_received);
          const notMatched = matchedFirms.filter(f => !f.is_match && !f.already_received);
          const sentCount = lead.assigned_firms?.length || 0;
          return (
          <div className="space-y-4 animate-fade-in">
            {/* Summary header */}
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-sm font-semibold">Send to Attorney Offices</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    <strong>{lead.case_category || lead.case_type || "Unknown case type"}</strong> · {lead.location || "Unknown location"}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-all text-xs font-semibold"
                    data-testid="send-section-share-btn"
                  >
                    <Link2 className="w-3.5 h-3.5" />Copy Share Link
                  </button>
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${lead.lead_type === "exclusive" ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "bg-sky-500/10 text-sky-400 border-sky-500/20"}`}>
                    {lead.lead_type === "exclusive" ? "EXCLUSIVE · 1 firm" : `SHARED · up to ${lead.max_recipients || 3}`}
                  </span>
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/30">
                    Sent to {sentCount}
                  </span>
                </div>
              </div>
            </div>

            {sendMsg && (
              <div className={`p-3 rounded-lg text-sm ${sendMsg.type === "success" ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" : "bg-destructive/10 border border-destructive/20 text-destructive"}`}>
                {sendMsg.text}
              </div>
            )}

            {/* ── SECTION 1: Already sent ── */}
            <div className="rounded-xl border border-violet-500/25 bg-violet-500/[0.04] overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-violet-500/20 bg-violet-500/[0.06]">
                <Send className="w-3.5 h-3.5 text-violet-400" />
                <p className="text-xs font-bold text-violet-300 uppercase tracking-wide">Sent To · {sendHistory.length}</p>
              </div>
              {sendHistory.length === 0 ? (
                <p className="px-4 py-4 text-xs text-muted-foreground">Not sent to any firm yet.</p>
              ) : (
                <div className="divide-y divide-border/40">
                  {sendHistory.map((h, i) => {
                    const isQualified = matchedFirms.find(f => f.id === h.firm_id)?.is_match;
                    return (
                    <div key={i} className="flex items-center gap-2 px-4 py-2.5 text-xs">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                      <span className="font-semibold text-emerald-300">{h.firm_name}</span>
                      {isQualified && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">QUALIFIED</span>}
                      <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${h.method === "manual" ? "bg-violet-500/15 text-violet-300" : "bg-sky-500/10 text-sky-400"}`}>{h.method}</span>
                      <span className="text-muted-foreground">{h.distributed_at ? new Date(h.distributed_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</span>
                      <button onClick={() => unsendFromFirm(h.firm_id)} disabled={sendingToFirm === h.firm_id}
                        className="ml-auto flex-shrink-0 py-1 px-2.5 rounded-md text-[11px] font-semibold border border-destructive/30 text-destructive hover:bg-destructive/10 transition disabled:opacity-60">
                        {sendingToFirm === h.firm_id ? "..." : "Unsend"}
                      </button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {firmsLoading ? (
              <div className="py-10 text-center text-sm text-muted-foreground">Finding firms...</div>
            ) : matchedFirms.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                No firm accounts found. Create or approve firms in Admin → Firms tab.
              </div>
            ) : (
              <>
                {/* ── SECTION 2: Qualified — ready to send ── */}
                <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.03] overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 border-b border-emerald-500/20 bg-emerald-500/[0.06]">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                    <p className="text-xs font-bold text-emerald-300 uppercase tracking-wide">Qualified — Ready to Send · {readyToSend.length}</p>
                    <span className="text-[11px] text-muted-foreground ml-auto">{matched.length} match this lead</span>
                  </div>
                  {readyToSend.length === 0 ? (
                    <p className="px-4 py-4 text-xs text-muted-foreground">{matched.length === 0 ? "No firms match this case type / location." : "All matched firms have already been sent this lead."}</p>
                  ) : (
                    <div className="divide-y divide-border/40">
                      {readyToSend.map(firm => (
                        <div key={firm.id} className="flex items-center gap-3 px-4 py-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-semibold">{firm.firm_name}</p>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${firm.tier === "enterprise" ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : firm.tier === "pro" ? "bg-primary/10 text-primary border-primary/20" : "bg-muted text-muted-foreground border-border"}`}>{firm.tier?.toUpperCase()}</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {firm.states_licensed?.join(", ") || "All states"}
                              {firm.package && <span className="ml-2 text-primary font-medium">{firm.package.remaining} leads left</span>}
                            </p>
                          </div>
                          <button onClick={() => sendToFirm(firm.id)}
                            disabled={!firm.can_receive || sendingToFirm === firm.id}
                            className={`flex-shrink-0 py-2 px-4 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${firm.can_receive ? "bg-emerald-500 text-white hover:bg-emerald-400" : "bg-muted text-muted-foreground cursor-not-allowed"} disabled:opacity-60`}>
                            {sendingToFirm === firm.id ? "Sending..." : !firm.has_balance ? "No Quota" : <><Send className="w-3 h-3" /> Send</>}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── SECTION 3: Not a match ── */}
                {notMatched.length > 0 && (
                  <div className="rounded-xl border border-border/40 bg-muted/10 overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/30">
                      <X className="w-3.5 h-3.5 text-muted-foreground" />
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Not a Match · {notMatched.length}</p>
                    </div>
                    <div className="divide-y divide-border/30">
                      {notMatched.map(firm => (
                        <div key={firm.id} className="flex items-center gap-3 px-4 py-2.5 opacity-70">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{firm.firm_name}</p>
                            <p className="text-[11px] text-muted-foreground">
                              {[!firm.specialty_match && "case type mismatch", !firm.location_match && "location mismatch"].filter(Boolean).join(" · ") || "Not matched"}
                            </p>
                          </div>
                          <button onClick={() => sendToFirm(firm.id)} disabled={sendingToFirm === firm.id}
                            className="flex-shrink-0 py-1.5 px-3 rounded-lg text-[11px] font-medium border border-border text-muted-foreground hover:bg-secondary/40 transition disabled:opacity-60">
                            {sendingToFirm === firm.id ? "Sending..." : "Send anyway"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          );
        })()}

        {activeSection === "notes" && (
          <div className="space-y-4 animate-fade-in" data-testid="notes-section">
            <div>
              <h2 className="text-sm font-semibold mb-1">Call Notes</h2>
              <p className="text-xs text-muted-foreground">Add notes as you cold call or follow up with this client.</p>
            </div>

            {/* Add note input */}
            <div className="rounded-xl border border-border/30 bg-card/20 p-4 space-y-3">
              <textarea
                ref={noteInputRef}
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleAddNote(); }}
                placeholder="Left a voicemail at 2:30 PM... / Spoke with client, they need a bilingual attorney... / Callback scheduled for Friday..."
                rows={3}
                className="w-full bg-secondary/30 border border-border/30 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/50"
                data-testid="note-input"
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Cmd/Ctrl + Enter to save</span>
                <button onClick={handleAddNote} disabled={addingNote || !noteText.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-all disabled:opacity-50"
                  data-testid="add-note-btn">
                  <Send className="w-3 h-3" />{addingNote ? "Saving..." : "Add Note"}
                </button>
              </div>
            </div>

            {/* Notes list */}
            {!lead.notes?.length ? (
              <div className="text-center py-12 text-muted-foreground">
                <StickyNote className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No notes yet</p>
                <p className="text-xs mt-1">Add your first note above</p>
              </div>
            ) : (
              <div className="space-y-2">
                {[...(lead.notes || [])].reverse().map(note => (
                  <div key={note.note_id} className="p-4 rounded-xl border border-border/30 bg-card/20 group" data-testid={`note-${note.note_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm leading-relaxed flex-1">{note.text}</p>
                      <button onClick={() => handleDeleteNote(note.note_id)}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all flex-shrink-0"
                        data-testid={`delete-note-${note.note_id}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-muted-foreground">{note.author}</span>
                      <span className="text-muted-foreground/30">·</span>
                      <span className="text-xs text-muted-foreground">{new Date(note.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {showShareModal && (
        <ShareLinkModal
          leadId={leadId}
          leadName={lead.name || "Anonymous Client"}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
}
